import { parseServerDate } from "src/utils/dateTime";
import { useLocale } from "../../../hooks/useLocale";
import { msg } from "../../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDateTime } from "src/utils/dateTime";
import { Box, HBox, VBox } from "@ui/box/Box";
import Button from "@ui/button/Button";
import Spacer from "@ui/spacer/Spacer";
import { ProgressBar } from "@ui/spinner/ProgressBar";
import { Typo } from "@ui/text/Typo";
import { useRef, useState, useEffect } from "react";
import { GD } from "src/GD";
import { IPagination, Pagination } from "../Pagination";
import { StatusBadge } from "../StatusBadge";
import { Table } from "@ui/table/Table";
import { ArchiveFileActions, isPreviewable, loadArchiveFileBlob, openDectaFilePopup } from "src/utils/dectaFile";
import { DateRangePicker, toUTCISODate } from "src/view/conponents/DateRangePicker";
import { HoverTooltip, FailReasonHint } from "../HoverTooltip";
import Input from "@ui/input/Input";
import { Select } from "@ui/select/Select";

interface IArchiveItem {
	id: number;
	yyyymm: number;
	status: "uploaded" | "uploading" | "completed" | "failed" | "added";
	created_at: string;
	file_name: string;
	file_path: string;
	fail_reason: string | null;
	file_created_date: string;
	prefix: string;
	data: string;
	empty?: boolean;
}

const perPage = 20;

const defaultFromDate = () => {
	const d = new Date();
	d.setUTCDate(d.getUTCDate() - 30);
	d.setUTCHours(0, 0, 0, 0);
	return d;
};

const formatDateParam = (date: Date): string => {
	const y = date.getUTCFullYear();
	const m = String(date.getUTCMonth() + 1).padStart(2, "0");
	const d = String(date.getUTCDate()).padStart(2, "0");
	return `${y}-${m}-${d}`;
};

export const Archive = () => {
	useLocale();
	const [currentPage, setCurrentPage] = useState<number>(0);
	const scrollRef = useRef<HTMLDivElement>(null);
	const [headerHeight] = GD.B_HEADER_HEIGHT.use(undefined, undefined, []);

	const [fromDate, setFromDate] = useState<Date>(defaultFromDate());
	const [toDate, setToDate] = useState<Date>(new Date());
	const [searchName, setSearchName] = useState<string>("");
	const [selectedPrefix, setSelectedPrefix] = useState<string>("");

	const prefixOptions = ["", "transact2", "mc_summary", "mc_incoming", "visa_summary", "visa_incoming", "reject", "chargeback", "chargeback_doc", "fraud", "out"];

	const getRequestData = (page: number) => ({
		method: "processing/archive",
		data: {
			page,
			limit: perPage,
			from_date: formatDateParam(fromDate),
			to_date: formatDateParam(toDate),
			...(searchName ? { name: searchName } : {}),
			...(selectedPrefix ? { prefix: selectedPrefix } : {}),
		},
	});

	const [response, reload, _reset, busy] = GD.REQ_API.useRequest(null, getRequestData(currentPage));

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
		reload(getRequestData(page));
	};

	const handleRefresh = () => {
		reload(getRequestData(currentPage));
	};

	const paginationData: IPagination | undefined = response?.data?.pagination;
	const items = response?.data?.items as IArchiveItem[] | undefined;
	const canOpenArchiveFile = (row: IArchiveItem) => !row.empty && (row.status === "uploaded" || row.status === "completed");

	const headerCount = 6;
	const stickyHeadSx = {
		position: "relative" as const,
		zIndex: 2,
		backgroundColor: "#d1dbe6",
		borderTop: "20px solid #f8fafc",
	};

	useEffect(() => {
		const wrapper = scrollRef.current;
		if (!wrapper || !items?.length) return;

		const grid = wrapper.firstElementChild as HTMLElement;
		if (!grid) return;

		const headerCells = Array.from(grid.children).slice(0, headerCount) as HTMLElement[];
		if (headerCells.length === 0) return;

		const stickyTop = headerHeight ?? 0;

		const handleScroll = () => {
			const wrapperRect = wrapper.getBoundingClientRect();
			const offset = stickyTop - wrapperRect.top;
			const maxOffset = grid.offsetHeight - headerCells[0].offsetHeight;
			const clamped = Math.round(Math.max(0, Math.min(offset, maxOffset)));

			for (const cell of headerCells) {
				cell.style.transform = clamped > 0 ? `translateY(${clamped}px)` : "";
			}
		};

		window.addEventListener("scroll", handleScroll, { passive: true });
		handleScroll();

		return () => window.removeEventListener("scroll", handleScroll);
	}, [items, headerHeight]);

	return (
		<VBox sx={{ flex: 1, minWidth: 0 }}>
			<Box
				sx={{
					width: "100%",
					display: "flex",
					flexWrap: "wrap",
					gap: "1px",
					backgroundColor: "rgba(0,0,0,0.06)",
					borderRadius: "4px",
					boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
				}}>
				<Box
					sx={{
						padding: "14px 20px",
						alignItems: "end",
						justifyContent: "flex-start",
						display: "flex",
						backgroundColor: "#fff",
						flex: "1 1 auto",
						gap: "8px",
						
					}}>

						<Input
							type="text"
							size="small"
							name="searchName"
							label={msg("fileName")}
							data={{ searchName }}
							onChange={v => setSearchName(v)}
							sx={{ minWidth: "160px" }}
						/>
						<Select
							size="small"
							name="selectedPrefix"
							label={msg("prefix")}
							data={{ selectedPrefix }}
							values={prefixOptions}
							onChange={v => setSelectedPrefix(v)}
							sx={{ minWidth: "160px" }}
						/>
						<DateRangePicker timeZone="UTC"
							style={{ width: "290px", maxWidth: "100%" }}
							label={msg("dateRange")}
							from={toUTCISODate(fromDate)}
							to={toUTCISODate(toDate)}
							onChange={(from, to) => {
								setFromDate(parseServerDate(from) ?? defaultFromDate());
								setToDate(parseServerDate(to) ?? new Date());
							}}
						/>
						<Button onClick={() => handleRefresh()} title={msg("search")} variant="primarySmall" />
					
				</Box>
			</Box>

			<Spacer size="10px" />
			<ProgressBar busy={busy} />

			{response?.error && (
				<Box sx={{ padding: "20px" }}>
					<Typo variant="medium" sx={{ color: "#DC2626" }}>
						{response.error.message ?? msg("errorLoadingArchive")}
					</Typo>
				</Box>
			)}

			{!busy && (
				<div ref={scrollRef} style={{ overflowX: "auto" }}>
					<Table<IArchiveItem>
						data={items ?? []}
						onRowClick={row => {
							if (!canOpenArchiveFile(row)) return;
							openDectaFilePopup({
								name: row.file_name,
								path: String(row.id),
								previewable: isPreviewable(row.file_name),
								load: () => loadArchiveFileBlob(row.id),
							});
						}}
						rowSx={row =>
							canOpenArchiveFile(row)
								? undefined
								: {
										cursor: "default",
										"&:hover > div": { backgroundColor: "#fff" },
									}
						}
						order={["created_at", "id", "file_name", "prefix", "status", "__actions" as any]}
						headers={[
							{ value: "Date", gridColumn: "auto", sx: stickyHeadSx },
							{ value: "ID", gridColumn: "auto", sx: stickyHeadSx },
							{ value: "File", gridColumn: "minmax(0, 1fr)", sx: stickyHeadSx },
							{ value: "Prefix", gridColumn: "auto", sx: stickyHeadSx },
							{ value: "Status", gridColumn: "auto", sx: stickyHeadSx },
							{ value: "", gridColumn: "auto", sx: stickyHeadSx },
						]}
						gridTemplateColumns="auto auto minmax(0, 1fr) auto auto auto"
						sx={{ backgroundColor: "#f8fafc" }}
						cellPadding="10px 18px"
						headPadding="10px 16px"
						cellFactory={(row, cell, _index, _key, name) => {
							if (name === "Date") {
								return {
									content: (
										<VBox>
											<Typo variant="medium"><DateTimeTooltip value={row.created_at}>{formatDisplayDateTime(row.created_at, { fallback: "" })}</DateTimeTooltip></Typo>
											<Typo variant="caption" color="textSecondary">
												ftp: <DateTimeTooltip value={row.file_created_date}>{formatDisplayDateTime(row.file_created_date, { fallback: "" })}</DateTimeTooltip>
											</Typo>
										</VBox>
									),
								};
							}
							if (name === "ID") {
								return { content: <Typo variant="medium">{cell}</Typo> };
							}
							if (name === "File") {
								return {
									content: (
										<VBox>
											<Typo variant="medium">{row.file_name}</Typo>
											<Typo variant="caption" color="textSecondary">
												{row.file_path}
											</Typo>
										</VBox>
									),
								};
							}
							if (name === "Prefix") {
								return { content: <Typo variant="medium">{cell}</Typo> };
							}
							if (name === "Status") {
								const isFailed = row.status === "failed";
								const statusContent = (
									<HBox gap="6px" sx={{ alignItems: "center" }}>
										<StatusBadge value={String(cell ?? "")} />
										{isFailed && row.fail_reason && <FailReasonHint />}
									</HBox>
								);
								if (isFailed && row.fail_reason) {
									return {
										content: (
											<HoverTooltip
												align="end"
												style={{ cursor: "pointer" }}
												tooltipStyle={{ maxWidth: "380px" }}
												content={
													<Typo variant="medium" sx={{ color: "#FFFFFF" }}>
														{row.fail_reason}
													</Typo>
												}>
												{statusContent}
											</HoverTooltip>
										),
									};
								}
								return { content: statusContent };
							}
							if (name === "") {
								if (row.empty) {
									return {
										content: (
											<HoverTooltip
												align="end"
												style={{ cursor: "default" }}
												tooltipStyle={{ maxWidth: "220px" }}
												content={
													<Typo variant="medium" sx={{ color: "#FFFFFF" }}>{msg("fileIsEmpty")}</Typo>
												}>
												<Box sx={{ padding: "4px", opacity: 0.5 }}>
													<svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#da744c" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
														<path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
														<polyline points="14 2 14 8 20 8" />
														<line x1="9" y1="15" x2="15" y2="15" />
													</svg>
												</Box>
											</HoverTooltip>
										),
										sx: { display: "flex", justifyContent: "end", alignItems: "center" },
									};
								}
								return {
									content: <ArchiveFileActions id={row.id} fileName={row.file_name} status={row.status} />,
									sx: { display: "flex", justifyContent: "end", alignItems: "center" },
								};
							}
							return { content: cell != null ? String(cell) : "" };
						}}
					/>
				</div>
			)}

			<Box
				sx={{
					display: "flex",
					gap: "10px",
					alignItems: "center",
					flexWrap: "wrap",
					position: "sticky",
					bottom: 0,
					zIndex: 2,
					backgroundColor: "#f8fafc",
					padding: "10px 0",
				}}>
				<Pagination pagination={paginationData} onPageChange={handlePageChange} />
				<Spacer grow />
				<Button onClick={() => handleRefresh()} title={msg("refresh")} variant="primarySmall" />
			</Box>
		</VBox>
	);
};
